/**
 * 1.180.0 Phase 6.3 — SSE activity stream hook.
 *
 * Opens an SSE connection via fetch + ReadableStream (NOT EventSource —
 * EventSource doesn't support custom Authorization headers; fetch +
 * ReadableStream supports both Bearer auth AND the standard SSE wire format).
 *
 * Reconnects with exponential backoff on disconnect (1s → 2s → 4s → 8s →
 * 15s cap). Uses an AbortController to cancel on unmount.
 */
import { useEffect } from 'react';
import type { ActivityEvent } from '@/services/knowledgeApi';

const _BACKOFF_INITIAL_MS = 1000;
const _BACKOFF_MAX_MS = 15000;

export function useActivityStream(
  onEvent: (event: ActivityEvent) => void,
  enabled = true,
) {
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let backoff = _BACKOFF_INITIAL_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let ac: AbortController | null = null;

    async function connect() {
      while (!cancelled) {
        try {
          ac = new AbortController();
          const token = localStorage.getItem('vibe_designer_token') || '';
          // services/api.ts always reads JSON; SSE needs response.body
          // (ReadableStream) for chunked frame parsing. Bearer header is
          // attached explicitly here — same pattern services/api.ts uses.
          // eslint-disable-next-line no-restricted-syntax
          const resp = await fetch('/api/knowledge/activity/stream', {
            headers: { Authorization: `Bearer ${token}` },
            signal: ac.signal,
          });
          if (!resp.ok || !resp.body) {
            throw new Error(`SSE connect failed: ${resp.status}`);
          }
          backoff = _BACKOFF_INITIAL_MS; // reset on successful connect
          const reader = resp.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (!cancelled) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const frames = buffer.split('\n\n');
            buffer = frames.pop() || ''; // keep last partial frame
            for (const frame of frames) {
              if (!frame.trim()) continue;
              if (frame.startsWith(':')) continue; // heartbeat comment
              const dataLine = frame
                .split('\n')
                .find((l) => l.startsWith('data:'));
              if (!dataLine) continue;
              try {
                const parsed = JSON.parse(
                  dataLine.slice(5).trim(),
                ) as ActivityEvent;
                onEvent(parsed);
              } catch {
                // malformed frame — ignore
              }
            }
          }
        } catch (_err) {
          if (cancelled) return;
          await new Promise<void>((resolve) => {
            timer = setTimeout(resolve, backoff);
          });
          backoff = Math.min(backoff * 2, _BACKOFF_MAX_MS);
        }
      }
    }
    void connect();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      ac?.abort();
    };
  }, [onEvent, enabled]);
}
